import { useCallback, useEffect, useRef, useState } from 'react';

import useQuestionAnimation from '@/features/timeGame/hooks/useQuestionAnimation';
import useTooltipFlow from '@/features/timeGame/hooks/useTooltipFlow';
import useResponsive from '@/shared/hooks/useResponsive';

import ControlBar from '@/features/timeGame/components/common/ControlBar';
import { AlienHandle } from '@/features/timeGame/components/elements/Alien';
import ConveyorBelt from '@/features/timeGame/components/elements/ConveyorBelt';
import LaunchConsole from '@/features/timeGame/components/elements/LaunchConsole';
import WarpTube from '@/features/timeGame/components/elements/WarpTube';
import AnswerBlock from '@/features/timeGame/components/questionAnswer/AnswerBlock';
import QuestionBlock from '@/features/timeGame/components/questionAnswer/QuestionBlock';

import BackgroundMobile from '@/features/timeGame/assets/images/game-screen-background-mobile.svg?react';
import Background from '@/features/timeGame/assets/images/game-screen-background.svg?react';

import '@/features/timeGame/styles/time-game-screen.scss';

import type { Question } from '@/features/gameSession/types';

const PANEL_TRANSITION_DURATION = 0.65;

interface GameScreenProps {
	currentQuestion: Question;
	currentQuestionIndex: number;
	currentAlienPosition: number;
	totalQuestions: number;
	correctAnswersCount: number;
	questionOrder: number[];
	onLaunch: () => void;
	onTimeChange: (hours: number, minutes: number) => void;
	checkAnswer: () => boolean;
	onBack: () => void;
	initialHours?: number;
	initialMinutes?: number;
	resetKey?: string | number;
	batteryLevel: number; // 0-5
	hasGameStarted?: boolean; // Track if game has started
	onQuestionReady?: () => void;
}

const GameScreen = ({
	currentQuestion,
	currentQuestionIndex,
	currentAlienPosition,
	totalQuestions,
	correctAnswersCount,
	questionOrder,
	onLaunch,
	onTimeChange,
	checkAnswer,
	onBack,
	initialHours,
	initialMinutes,
	resetKey,
	batteryLevel,
	hasGameStarted = false,
	onQuestionReady,
}: GameScreenProps) => {
	const { isPortrait } = useResponsive();

	const { tooltipState, onAnswerBlockAnimationStart, onAnswerBlockInteraction, hideAllTooltips } = useTooltipFlow({
		isFirstQuestion: currentQuestionIndex === 0,
		hasGameStarted,
	});

	const {
		aliensContainerRef,
		questionBlockRef,
		answerBlockRef,
		setConveyorAnimationRef,
		playQuestionAnimation,
		playAnswerAnimation,
		playExitAnimation,
	} = useQuestionAnimation({ onAnswerBlockAnimationStart });
	const alienHandlesRef = useRef<Record<number, AlienHandle | null>>({});
	const questionAnimationRunRef = useRef(0);

	// Track current user answer
	const currentAnswerRef = useRef({
		hours: initialHours || 0,
		minutes: initialMinutes || 0,
	});
	const [isConsoleLocked, setIsConsoleLocked] = useState(true);

	const questionType = currentQuestion.type || '';

	const textContent = currentQuestion.question_display || '';

	const needsDigitalAnswer = questionType.includes('ToDigital');

	const BackgroundComponent = isPortrait ? BackgroundMobile : Background;

	// Update answer when user changes time
	const handleTimeChange = useCallback(
		(hours: number, minutes: number) => {
			currentAnswerRef.current = { hours, minutes };
			onTimeChange(hours, minutes);
		},
		[onTimeChange],
	);

	const handleLaunch = useCallback(async () => {
		setIsConsoleLocked(true);
		hideAllTooltips();

		const isCorrect = checkAnswer();
		await playAnswerAnimation(isCorrect, alienHandlesRef.current[currentQuestionIndex] ?? undefined);
		await playExitAnimation();
		onLaunch();
	}, [checkAnswer, playAnswerAnimation, playExitAnimation, onLaunch, currentQuestionIndex, hideAllTooltips]);

	// Play question animation when game starts (game screen becomes visible)
	useEffect(() => {
		if (!hasGameStarted) {
			setIsConsoleLocked(true);
			return;
		}

		questionAnimationRunRef.current += 1;
		const animationRunId = questionAnimationRunRef.current;
		setIsConsoleLocked(true);

		// Delay animation slightly to allow panel transition to complete
		const timer = setTimeout(async () => {
			await playQuestionAnimation(currentAlienPosition);
			if (animationRunId === questionAnimationRunRef.current) {
				setIsConsoleLocked(false);
				onQuestionReady?.();
			}
		}, PANEL_TRANSITION_DURATION * 1000);

		return () => {
			clearTimeout(timer);
		};
	}, [currentAlienPosition, currentQuestionIndex, hasGameStarted, playQuestionAnimation, onQuestionReady]);

	return (
		<div className="time-game-screen">
			<div className="time-game-screen__background">
				<BackgroundComponent preserveAspectRatio="xMidYMax slice" />
			</div>

			<div className="time-game-screen__controls">
				<ControlBar onBack={onBack} small />
			</div>

			<div className="time-game-screen__question-block" ref={questionBlockRef}>
				<QuestionBlock questionType={questionType} textContent={textContent} />
			</div>

			<div className="time-game-screen__answer-block" ref={answerBlockRef}>
				<AnswerBlock
					answerType={needsDigitalAnswer ? 'digital' : 'analog'}
					onChange={handleTimeChange}
					initialHours={initialHours}
					initialMinutes={initialMinutes}
					resetKey={resetKey}
					isTooltipOpen={tooltipState.showAnswerBlockTooltip}
					onInteraction={onAnswerBlockInteraction}
				/>
			</div>

			<div className="time-game-screen__conveyor" ref={aliensContainerRef}>
				<ConveyorBelt
					correctAnswersCount={correctAnswersCount}
					totalQuestions={totalQuestions}
					questionOrder={questionOrder}
					onAlienHandleReady={(questionIndex, handle) => {
						alienHandlesRef.current[questionIndex] = handle;
					}}
					onConveyorAnimationReady={setConveyorAnimationRef}
				/>
			</div>

			<div className="time-game-screen__warp-tube">
				<WarpTube batteryLevel={batteryLevel} isBatteryTooltipOpen={tooltipState.showBatteryTooltip} />
			</div>

			<div className="time-game-screen__console">
				<LaunchConsole
					onLaunch={handleLaunch}
					disabled={isConsoleLocked}
					isTooltipOpen={tooltipState.showLaunchConsoleTooltip}
				/>
			</div>
		</div>
	);
};

export default GameScreen;
